import React from 'react'

import { useIntl } from 'react-intl'
import { TableColumn } from 'react-data-table-component'

import { UserRole } from '@/types'
import useUser from '@/hooks/useUser'
import DataTable from '../DataTable/DataTable'
import ShareLoanEmptyState from './ShareLoanEmptyState'
import ShareLoanActionsCell from './ShareLoanActionsCell'
import ShareLoanListNameCell from './ShareLoanListNameCell'
import type { SelectChangeEvent } from '../formControls/Select'
import ShareLoanAssignedRoleCell from './ShareLoanAssignedRoleCell'

type ShareLoanListProps = {
	data: UserRole[]
	loading?: boolean
	submitting?: boolean
	showRolesBasedOnUserRole?: boolean
	onRoleItemChange?: (
		e: SelectChangeEvent<unknown>,
		userId: string,
		rowIndex: number
	) => void
	onRevokeUser?: (userId: string, rowIndex: number) => void
}

function checkIsDisabledAction(
	row: UserRole,
	userId: string,
	submitting: boolean = false
) {
	return (
		row.loadingRevoke ||
		row.loadingEdit ||
		row.user.id === userId ||
		submitting
	)
}

const ShareLoanList = ({
	data,
	loading,
	submitting,
	onRevokeUser,
	onRoleItemChange,
	showRolesBasedOnUserRole = true,
}: ShareLoanListProps) => {
	const { formatMessage } = useIntl()
	const { user } = useUser()

	const columns: TableColumn<UserRole>[] = [
		{
			name: formatMessage({
				id: 'share_loan.name',
			}),
			selector: (row) => row.user.name,
			cell: (row) => <ShareLoanListNameCell row={row} />,
		},
		{
			name: formatMessage({
				id: 'share_loan.assigned_role',
			}),
			selector: (row) => row.assignedRole,
			cell: (row, rowIndex) => (
				<ShareLoanAssignedRoleCell
					row={row}
					disabled={checkIsDisabledAction(row, user.id, submitting)}
					showRolesBasedOnUserRole={showRolesBasedOnUserRole}
					onChangeRoleItem={(value, userId) =>
						onRoleItemChange?.(value, userId, rowIndex)
					}
				/>
			),
		},
		{
			name: '',
			cell: (row, rowIndex) => (
				<ShareLoanActionsCell
					row={row}
					disabled={checkIsDisabledAction(row, user.id, submitting)}
					onRevokeUser={(userId) => {
						onRevokeUser?.(userId, rowIndex)
					}}
				/>
			),
			ignoreRowClick: true,
			allowOverflow: true,
			button: true,
		},
	]

	return (
		<DataTable
			keyField="id"
			data={data}
			noDataComponent={<ShareLoanEmptyState />}
			progressPending={loading}
			columns={columns}
			pagination={false}
		/>
	)
}

export default ShareLoanList
